import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAppSelector } from "../../hooks/redux";
import PageLayout from "../../components/shared/PageLayout";
import QuestionReadyContent from "../../components/anonymous/QuestionReadyContent";
import QuestionReadyActions from "../../components/anonymous/QuestionReadyActions";
import QuestionReadyLinks from "../../components/anonymous/QuestionReadyLinks";
import BackgroundSelectionModal from "../../components/anonymous/BackgroundSelectionModal";
import { StyledQuestionDetail } from "./index.styled";
import { fetchBoxByShortCode } from "../../utils/apiService";
import { downloadQuestionAsImage } from "../../utils/downloadUtils";
import { showToast } from "../../utils/toastUtils";
import type { CreatedBox } from "../../types/dashboard";

const QuestionDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const questions = useAppSelector(
    (state) => state.dashboard.data.anonymousQuestions,
  );

  const [questionFromApi, setQuestionFromApi] = useState<CreatedBox | null>(
    null,
  );
  const [isBackgroundModalOpen, setIsBackgroundModalOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const previewRef = useRef<HTMLDivElement>(null);

  const boxTitle =
    questionFromApi?.title ?? questions.find((q) => q.short_code === id)?.content ?? "";

  const handleExport = async () => {
    if (previewRef.current && id) {
      setIsExporting(true);
      try {
        await downloadQuestionAsImage(previewRef.current, id);
        showToast("התמונה הורדה בהצלחה!");
      } finally {
        setIsExporting(false);
      }
    }
  };

  useEffect(() => {
    if (!id) return;

    const loadQuestion = async () => {
      try {
        const box = await fetchBoxByShortCode(id);
        setQuestionFromApi(box);
      } catch (error) {
        console.error("Failed to fetch question by short code", error);
      }
    };

    void loadQuestion();
  }, [id]);

  return (
    <PageLayout
      title="שאלות אנונימיות"
      StyledComponent={StyledQuestionDetail}
      containerClassName="question-detail-container"
      onBack={() => navigate("/anonymous")}
    >
      <QuestionReadyContent
        questionContent={boxTitle}
        previewRef={previewRef}
      />
      <QuestionReadyActions
        onExport={handleExport}
        onChooseBackground={() => setIsBackgroundModalOpen(true)}
        isLoading={isExporting}
      />
      <QuestionReadyLinks
        publicId={questionFromApi?.public_id}
        shortCode={id}
      />

      <BackgroundSelectionModal
        isOpen={isBackgroundModalOpen}
        onClose={() => setIsBackgroundModalOpen(false)}
        elementRef={previewRef}
        id={id}
        onDownloaded={() => showToast("התמונה הורדה בהצלחה!")}
      />
    </PageLayout>
  );
};

export default QuestionDetail;
